import {assert} from 'Asserts';
import {GraphQLAnalyticsDashboardSpec as AnalyticsDashboard} from 'insights/analytics-dashboards/data/__generated/GraphQLAnalyticsDashboardSpec';

import {MAXIMUM_ROW_LENGTH} from './LayoutConstants';

function generateErrorMessage(message: string, additionalMessage: string): string {
  return message.concat(additionalMessage === '' ? '' : ' ', additionalMessage);
}

/**
 * Checks for both malformed layout shapes and orphan queries. It is helpful to use this assertion in code
 * where layouts play a critical part (e.g. rendering a layout or making spec requests). These assertions will
 * help catch developers errors such as forgotten layout updates or incomplete test fixtures.
 *
 * @param analyticsDashboard Dashboard to test.
 * @param additionalMessage Additional text to add to the various assertions. While the grammar might not make
 * sense, using this parameter can help pin point where the error came from in other debug tools such as Sentry
 */
export default function assertWellFormedLayout(
  analyticsDashboard: AnalyticsDashboard,
  additionalMessage: string
): void {
  const renderedBlocksCount = analyticsDashboard.layoutRows
    .map((row, rowIndex) => {
      const rowLength = row.analyticsQueries.length;
      // TODO(DnDM2.3) Remove restriction where every row except the last must be full
      assert(
        rowLength === MAXIMUM_ROW_LENGTH || rowIndex === analyticsDashboard.layoutRows.length - 1,
        generateErrorMessage(
          `Expected every row except the last row to have exactly ${MAXIMUM_ROW_LENGTH} analyticsQueryIds`,
          additionalMessage
        )
      );
      assert(
        rowLength <= MAXIMUM_ROW_LENGTH,
        generateErrorMessage(
          `Expected row to have at most ${MAXIMUM_ROW_LENGTH} analyticsQueryIds`,
          additionalMessage
        )
      );
      assert(
        rowLength > 0,
        generateErrorMessage('Expected row to have at least 1 analyticsQueryIds', additionalMessage)
      );
      return rowLength;
    })
    .reduce((accumulator, rowLength) => accumulator + rowLength, 0);

  assert(
    renderedBlocksCount === analyticsDashboard.analyticsQueries.length,
    generateErrorMessage('Found unexpected orphan blocks while rendering block layout', additionalMessage)
  );
}
